import React, { useState } from 'react';
import { Calendar, Printer, Download, Search, CheckCircle, AlertCircle, Package } from 'lucide-react';
import { generateThermalLabelsPDF, generateCompactThermalLabelsPDF } from './pdfGenerator';
import { supabase } from './supabase';

interface PrintOrdersToolProps {
    onClose?: () => void;
}

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: 'nueva', label: 'Nueva' },
    { value: 'falta_insumo', label: 'Falta insumo' },
    { value: 'cortado', label: 'Cortado' },
    { value: 'empaquetado', label: 'Empaquetado' },
    { value: 'entregado', label: 'Entregado' },
    { value: 'cancelada', label: 'Cancelada' },
];

export default function PrintOrdersTool({ onClose }: PrintOrdersToolProps) {
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });
    const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [matchingOrders, setMatchingOrders] = useState<any[]>([]);
    const [searched, setSearched] = useState(false);

    const toggleStatus = (status: string) => {
        setStatusFilter(prev => {
            const next = new Set(prev);
            if (next.has(status)) next.delete(status); else next.add(status);
            return next;
        });
    };

    const fetchOrders = async () => {
        setLoading(true);
        setSearched(true);
        try {
            let query = supabase
                .from('orders')
                .select('*')
                .is('deleted_at', null)
                .gte('created_at', `${startDate}T00:00:00`)
                .lte('created_at', `${endDate}T23:59:59`)
                .order('created_at', { ascending: true });

            if (statusFilter.size > 0) {
                query = query.in('status', Array.from(statusFilter));
            }

            const { data, error } = await query;

            if (error) throw error;
            setMatchingOrders(data || []);
        } catch (err) {
            console.error('Error fetching orders for printing:', err);
            alert('Error al buscar órdenes');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        if (matchingOrders.length === 0) return;
        setLoading(true);
        try {
            await generateThermalLabelsPDF(matchingOrders);
        } catch (err) {
            console.error('Error generating PDF:', err);
            alert('Error al generar el PDF');
        } finally {
            setLoading(false);
        }
    };

    const handlePrintCompact = async (perPage: 2 | 4) => {
        if (matchingOrders.length === 0) return;
        setLoading(true);
        try {
            await generateCompactThermalLabelsPDF(matchingOrders, perPage);
        } catch (err) {
            console.error('Error generating compact PDF:', err);
            alert('Error al generar el PDF compacto');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="print-tool-container" style={{
            padding: '24px',
            backgroundColor: 'var(--card-bg)',
            borderRadius: '16px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--border-color)',
            maxWidth: '600px',
            margin: '0 auto'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                    backgroundColor: '#991b1b15',
                    padding: '10px',
                    borderRadius: '12px',
                    color: '#991b1b'
                }}>
                    <Printer size={24} />
                </div>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>Imprenta de Etiquetas</h2>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Generar PDF para impresora térmica (100x150mm)</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div className="input-group">
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase' }}>Desde</label>
                    <div style={{ position: 'relative' }}>
                        <Calendar size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px 10px 40px',
                                borderRadius: '10px',
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--input-bg)',
                                color: 'var(--text-main)',
                                fontSize: '0.95rem'
                            }}
                        />
                    </div>
                </div>
                <div className="input-group">
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase' }}>Hasta</label>
                    <div style={{ position: 'relative' }}>
                        <Calendar size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px 10px 40px',
                                borderRadius: '10px',
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--input-bg)',
                                color: 'var(--text-main)',
                                fontSize: '0.95rem'
                            }}
                        />
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        Estado del pedido
                    </label>
                    {statusFilter.size > 0 && (
                        <button
                            onClick={() => setStatusFilter(new Set())}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-muted)',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: 0,
                            }}
                        >
                            Limpiar
                        </button>
                    )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {STATUS_FILTER_OPTIONS.map(opt => {
                        const active = statusFilter.has(opt.value);
                        return (
                            <button
                                key={opt.value}
                                onClick={() => toggleStatus(opt.value)}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '999px',
                                    border: `1px solid ${active ? 'var(--primary-color)' : 'var(--border-color)'}`,
                                    backgroundColor: active ? 'var(--primary-color)' : 'transparent',
                                    color: active ? 'white' : 'var(--text-main)',
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {statusFilter.size === 0 ? 'Sin filtro: incluye todos los estados.' : `Filtrando ${statusFilter.size} ${statusFilter.size === 1 ? 'estado' : 'estados'}.`}
                </p>
            </div>

            <button
                onClick={fetchOrders}
                disabled={loading}
                style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    marginBottom: '24px'
                }}
                className="btn-search"
            >
                {loading ? <div className="spinner-small" /> : <Search size={18} />}
                Buscar Órdenes
            </button>

            {searched && (
                <div style={{
                    padding: '20px',
                    borderRadius: '12px',
                    backgroundColor: matchingOrders.length > 0 ? '#16a34a0a' : '#ef44440a',
                    border: `1px solid ${matchingOrders.length > 0 ? '#16a34a20' : '#ef444420'}`,
                    textAlign: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                        {matchingOrders.length > 0 ? (
                            <CheckCircle size={20} color="#16a34a" />
                        ) : (
                            <AlertCircle size={20} color="#ef4444" />
                        )}
                        <span style={{ fontWeight: 600, color: matchingOrders.length > 0 ? '#16a34a' : '#ef4444' }}>
                            {matchingOrders.length} {matchingOrders.length === 1 ? 'orden encontrada' : 'órdenes encontradas'}
                        </span>
                    </div>

                    {matchingOrders.length > 0 && (
                        <>
                            <p style={{ margin: '0 0 16px 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                                Elegí el formato para generar el PDF.
                            </p>
                            <button
                                onClick={handlePrint}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    borderRadius: '12px',
                                    backgroundColor: '#16a34a',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    fontSize: '1rem',
                                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)',
                                    marginBottom: '10px'
                                }}
                            >
                                <Download size={20} />
                                COMPLETAS · 100×150mm · 1 por hoja
                            </button>
                            <button
                                onClick={() => handlePrintCompact(2)}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    borderRadius: '12px',
                                    backgroundColor: '#1f2937',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    fontSize: '1rem',
                                    boxShadow: '0 4px 12px rgba(31, 41, 55, 0.2)',
                                    marginBottom: '10px'
                                }}
                            >
                                <Download size={20} />
                                COMPACTAS · 100×150mm · vertical, ~2 por hoja
                            </button>
                            <button
                                onClick={() => handlePrintCompact(4)}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    borderRadius: '12px',
                                    backgroundColor: '#374151',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    fontSize: '1rem',
                                    boxShadow: '0 4px 12px rgba(55, 65, 81, 0.2)'
                                }}
                            >
                                <Download size={20} />
                                COMPACTAS · 100×150mm · vertical, denso (~4 por hoja)
                            </button>
                            <p style={{ margin: '10px 0 0 0', fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                                Compactas: nº pedido + cliente + items con check, ideal para producción.
                            </p>
                        </>
                    )}
                </div>
            )}

            {onClose && (
                <button
                    onClick={onClose}
                    style={{
                        marginTop: '16px',
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text-muted)',
                        fontSize: '0.875rem',
                        cursor: 'pointer'
                    }}
                >
                    Cancelar
                </button>
            )}
        </div>
    );
}
